import { Button, Space, Switch, Tag } from "@bedrock/components";
import { TagColor } from "@bedrock/components/lib/Tag";
import DotDotDot from "@mpc/common/src/components/DotDotDot";
import CopyableNode from "@mpc/common/src/components/CopyableNode";
import moment from "moment";
import React from "react";
import CurrencyType from '@mpc/common/src/components/SForm/SFormElement/CurrencyType';
import _ from "lodash";
import { MPCListProps } from "@mpc/common/src/features/MPCList";
import AmountOrder from "mpc-common/components/AmountOrder";

export enum FIRST_LEVEL_ENUM {
  outNormalPurchase = 'outNormalPurchase',
  outBiddingPurchase = 'outBiddingPurchase',
}

const demandCustomColumnsConf = ({ handleDealChange, secondLevel }: any = {}) => {
  return {
    plan_demand_id: {
      fixed: 'left',
      width: 96,
      render: (v: any, r: any) => {
        return <CopyableNode value={v} tipToolTitle="复制单号" />;
      },
    },
    leaf_demand_id: {
      fixed: 'left',
      width: 96,
      render: (v: any, r: any) => {
        return <CopyableNode value={v} tipToolTitle="复制单号" />;
      },
    },
    id: {
      width: 104,
      fixed: 'left',
      render: (v: any, r: any, i: number) => {
        return (
          <CopyableNode value={v} tipToolTitle="复制单号" />
        )
      }
    },
    state_text: {
      width: 120,
      render: (v: any, r: any, i: number) => {
        return <div className="fw-bold">{v}</div>
      },
    },
    name: {
      width: 360,
      render: (v: any, r: any, i: number) => {
        return (
          <div className="one-rows-ellipsis" title={v}>
            {r.backed ? <Tag color="light-red" size="xSmall" text="被退回" className="m-r-4"/> : null}
            {v}
          </div>
        )
      }
    },
    quote_deadline: {
      align: 'left',
      width: 268,
      render: (v: any, r: any, i: number) => {
        const diffHours = moment(v).diff(moment().startOf('days'), 'hours');
        let info = '';
        let color: TagColor = 'light-grey';
        if (diffHours < 0) {
          info = '已过期';
          color = 'light-grey';
        } else if (diffHours >= 0 && diffHours < 24) {
          info = '今天过期';
          color = 'light-red';
        } else if (diffHours >= 24) {
          const diffDays = _.round(diffHours / 24, 0);
          if (diffDays <= 3) {
            info = `剩余${diffDays}天过期`;
            color = 'light-orange';
          }
        }
        return (
          <Space size="xxs">
            {v}
            {info ? 
              <Tag
                size="xSmall"
                color={color}
                text={info}
              /> : null}
          </Space>
        );
      },
    },
    finished: {
      width: 160,
      align: 'left',
      fixed: 'right',
      render: (v: any, r: any, i: number) => {
        return (
          <Space size="xs" onClick={e => { e.stopPropagation(); }} >
            <Switch
              checked={!v}
              labelOn="待处理"
              labelOff="已处理"
              onChange={(e: any) => {
                handleDealChange({
                  finished: !r.finished,
                  id: r.id,
                })
              }}
            />
          </Space>
        );
      },
    },
    quote_price: {
      fixed: [DEMAND_TAB_KEY.settledUpload, DEMAND_TAB_KEY.pendingQuote].includes(secondLevel) ? undefined : 'right',
      width: 170,
      align: 'right',
      render: (v: any, r: any, i: number) => {
        return (
          <CurrencyType
            view
            value={{
              value: v,
              currency: r?.quote_price_currency,
            }}
            currencyColor={'black'}
          />
        );
      },
    },
    settle_price: {
      fixed: 'right',
      width: 170,
      align: 'right',
      render: (v: any, r: any, i: number) => {
        return (
          <CurrencyType
            view
            value={{
              value: v,
              currency: r?.settle_price_currency,
            }}
          />
        );
      },
    },
    operation: {
      fixed: 'right',
      render: (v: any, r: any, i: number) => {
        return r?.permit_actions?.can_move ? (
          <Button type='link'>处理</Button>
        ) : null;
      },
    },
  };
};

export enum DEMAND_TAB_KEY {
  pendingQuote = 'pendingQuotePortal',
  making = 'makingPortal',
  settledUpload = 'settledUploadPortal',
  allDemand = 'allDemandPortal',
}

export const demandTabItems = [
  {
    key: DEMAND_TAB_KEY.pendingQuote,
    tab: '待报价',
  },
  {
    key: DEMAND_TAB_KEY.making,
    tab: '待制作',
  },
  {
    key: DEMAND_TAB_KEY.settledUpload,
    tab: '发票待上传',
  },
  {
    key: DEMAND_TAB_KEY.allDemand,
    tab: '全部',
  }
];

const demandFilterOptions: MPCListProps['filterOptions'] = {
  filterKeys: [
    {
      searchKey: 'state_list',
      optionKey: 'wb_demand_states',
    },
    {
      searchKey: 'product',
      optionKey: 'wb_new_products'
    },
    {
      searchKey: 'demand_categories',
      optionKey: 'demand_categories'
    },
    {
      searchKey: 'quote_deadline_range',
      optionKey: 'quote_deadline_range',
      name: '报价截止时间',
    },
    {
      searchKey: 'expect_end_at_range',
      optionKey: 'expect_end_at_range',
      name: '预计发布时间',
    },
    {
      searchKey: 'backed',
      optionKey: 'wb_demand_back',
      name: '仅展示被退回'
    },
  ]
}

const biddingCustomColumnsConf = {
  id: {
    fixed: 'left',
    width: 96,
    render: (v: any, r: any) => {
      return <CopyableNode value={v} tipToolTitle="复制单号" ><Button type='link'>{v}</Button></CopyableNode>;
    },
  },
  product_identifier: {
    width: 104,
    align: 'left',
  },
  name: {
    width: 240,
    align: 'left',
    render: (v: any) => <DotDotDot clamp={1}>{v || '-'}</DotDotDot>,
  },
  budget_text: {
    width: 200,
    align: 'right',
    render: (v: string, r: { budget: any, currency_enum: any; }) => (
      <CurrencyType
        view
        value={{
          value: r?.budget,
          currency: r?.currency_enum,
        }}
        currencyColor={'black'}
      />
    )
  },
  // budget_text: {
  //   width: 200,
  //   align: 'right',
  //   render: (v: string) => (
  //     <div className="fw-bold" style={{ display: 'inline-grid'}}>
  //       <div>{v}</div> 
  //       <div style={{ justifySelf: 'start' }}>
  //         <AmountOrder value={v?.split(' ')[0]} />
  //       </div>
  //     </div>
  //   )
  // },
  bidding_method: {
    width: 120,
    align: 'left',
  },
  bidding_type: {
    width: 160,
    align: 'left',
  },
  businesses_names: {
    align: 'left',
    width: 160,
    render: (v: any, r: any) => {
      const text = Array.isArray(v) ? v.join('，') : v;
      return <DotDotDot clamp={1}>{text || '-'}</DotDotDot>
    },
  },
  bidding_state_text: {
    width: 200,
    align: 'left',
  },
  operation: {
    width: 60,
    fixed: 'right',
    render: (v: any, r: any) => {
      return (r['can_move?'] || r['can_move']) &&
        (r['state'] || '').toLowerCase() !== 'closed' && <Button type='link'>处理</Button>
    }
  },
}

export enum BIDDING_TAB_KEY {
  toDoBidding = 'toDoBidding',
  doneBidding = 'doneBidding',
}

export const biddingTabItems = [
  {
    key: BIDDING_TAB_KEY.toDoBidding,
    tab: '待办',
  },
  {
    key: BIDDING_TAB_KEY.doneBidding,
    tab: '已办',
  },
];

const biddingFilterOptions = {
  filterKeys: [
    // {
    //   searchKey: 'state',
    //   optionKey: 'state',
    // },
    // {
    //   searchKey: 'product',
    //   optionKey: 'wb_prdoucts'
    // },
    {
      searchKey: 'bidding_types',
      optionKey: 'bidding_types'
    },
    {
      searchKey: 'bidding_methods',
      optionKey: 'bidding_methods'
    },
    // {
    //   searchKey: 'invite_confirm_end_time_range',
    //   optionKey: 'invite_confirm_end_time_range'
    // },
    // {
    //   searchKey: 'submit_plan_end_time_range',
    //   optionKey: 'submit_plan_end_time_range'
    // },
  ]
}

const omitFilter = (filterOptions: MPCListProps['filterOptions'], keys: string[]) => {
  const data = filterOptions?.filterKeys?.filter((item) => !keys.includes(typeof item === 'string' ? item : item.searchKey));
  return {
    filterKeys: data,
  }
}

export const LIST_CONF = {
  [FIRST_LEVEL_ENUM.outNormalPurchase]: {
    [DEMAND_TAB_KEY.pendingQuote]: {
      customColumnsConf: demandCustomColumnsConf,
      filterOptions: omitFilter(demandFilterOptions, ['state_list']),
    },
    [DEMAND_TAB_KEY.making]: {
      customColumnsConf: demandCustomColumnsConf,
      filterOptions: omitFilter(demandFilterOptions, ['state_list', 'quote_deadline_range']),
    },
    [DEMAND_TAB_KEY.settledUpload]: {
      customColumnsConf: demandCustomColumnsConf,
      filterOptions: omitFilter(demandFilterOptions, ['state_list', 'quote_deadline_range', 'expect_end_at_range']),
    },
    [DEMAND_TAB_KEY.allDemand]: {
      customColumnsConf: demandCustomColumnsConf,
      filterOptions: demandFilterOptions,
    }
  },
  [FIRST_LEVEL_ENUM.outBiddingPurchase]: { 
    [BIDDING_TAB_KEY.toDoBidding]: {
      customColumnsConf: () => biddingCustomColumnsConf,
      filterOptions: biddingFilterOptions,
    },
    [BIDDING_TAB_KEY.doneBidding]: {
      customColumnsConf: () => biddingCustomColumnsConf,
      filterOptions: biddingFilterOptions,
    }
  }
}